/**
 * Enhanced Invoice Service
 * Handles all enhanced invoice management operations
 */

import { apiRequest } from '../utils/apiResponseHandler';
import { getApiUrl } from '../config/api';

const enhancedInvoiceService = {
  // ==================== ENHANCED INVOICE MANAGEMENT ====================

  // Preview Invoice
  async previewInvoice(invoiceId) {
    return await apiRequest(getApiUrl('INVOICE_PREVIEW', invoiceId), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Bulk Preview Invoices
  async bulkPreviewInvoices(previewData) {
    return await apiRequest(getApiUrl('INVOICE_BULK_PREVIEW'), {
      method: 'POST',
      body: previewData,
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Get Invoice Templates
  async getInvoiceTemplates(filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });
    
    const url = queryParams.toString() 
      ? `${getApiUrl('INVOICE_TEMPLATES')}?${queryParams.toString()}`
      : getApiUrl('INVOICE_TEMPLATES');
    
    return await apiRequest(url, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Get Invoice Dashboard
  async getInvoiceDashboard() {
    return await apiRequest(getApiUrl('INVOICE_DASHBOARD'), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Validate Invoice Data
  async validateInvoiceData(invoiceData) {
    return await apiRequest(getApiUrl('INVOICE_VALIDATE'), {
      method: 'POST',
      body: invoiceData,
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // ==================== BULK INVOICE OPERATIONS ====================

  // Get Bulk Invoice Operations Dashboard
  async getBulkOperationsDashboard() {
    return await apiRequest(getApiUrl('BULK_INVOICE_OPERATIONS_DASHBOARD'), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Create Annual Renewal
  async createAnnualRenewal(renewalData) {
    return await apiRequest(getApiUrl('BULK_INVOICE_OPERATIONS_CREATE_ANNUAL_RENEWAL'), {
      method: 'POST',
      body: renewalData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Get Bulk Operation Details
  async getBulkOperationDetails(operationId) {
    return await apiRequest(getApiUrl('BULK_INVOICE_OPERATION_BY_ID', operationId), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Approve Bulk Operation
  async approveBulkOperation(operationId, approvalData) {
    return await apiRequest(getApiUrl('BULK_INVOICE_OPERATION_APPROVE', operationId), {
      method: 'POST',
      body: approvalData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Cancel Bulk Operation
  async cancelBulkOperation(operationId, cancelData) {
    return await apiRequest(getApiUrl('BULK_INVOICE_OPERATION_CANCEL', operationId), {
      method: 'POST',
      body: cancelData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Get Bulk Operation Members
  async getBulkOperationMembers(operationId, filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });
    
    const url = queryParams.toString() 
      ? `${getApiUrl('BULK_INVOICE_OPERATION_MEMBERS', operationId)}?${queryParams.toString()}`
      : getApiUrl('BULK_INVOICE_OPERATION_MEMBERS', operationId);
    
    return await apiRequest(url, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Get Bulk Operation Logs
  async getBulkOperationLogs(operationId, filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });
    
    const url = queryParams.toString() 
      ? `${getApiUrl('BULK_INVOICE_OPERATION_LOGS', operationId)}?${queryParams.toString()}`
      : getApiUrl('BULK_INVOICE_OPERATION_LOGS', operationId);
    
    return await apiRequest(url, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Get Bulk Operation Summary
  async getBulkOperationSummary(operationId) {
    return await apiRequest(getApiUrl('BULK_INVOICE_OPERATION_SUMMARY', operationId), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Bulk Operation Actions
  async bulkOperationAction(actionData) {
    return await apiRequest(getApiUrl('BULK_INVOICE_OPERATION_ACTION'), {
      method: 'POST',
      body: actionData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // ==================== UTILITY FUNCTIONS ====================

  // Validate Bulk Operation Data
  validateBulkOperationData(operationData) {
    const errors = {};
    
    if (!operationData.title?.trim()) {
      errors.title = 'Title is required';
    }
    
    if (!operationData.operation_type) {
      errors.operation_type = 'Operation type is required';
    }
    
    if (!operationData.financial_period?.trim()) {
      errors.financial_period = 'Financial period is required';
    }
    
    if (!operationData.invoice_amount || operationData.invoice_amount <= 0) {
      errors.invoice_amount = 'Valid invoice amount is required';
    }
    
    if (!operationData.due_days || operationData.due_days < 1) {
      errors.due_days = 'Due days must be at least 1';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  // Format Bulk Operation Data for API
  formatBulkOperationDataForAPI(operationData) {
    return {
      title: operationData.title?.trim(),
      operation_type: operationData.operation_type,
      description: operationData.description?.trim() || '',
      financial_period: operationData.financial_period?.trim(),
      invoice_amount: parseFloat(operationData.invoice_amount),
      due_days: parseInt(operationData.due_days),
      send_immediately: operationData.send_immediately === true,
      send_emails: operationData.send_emails !== false,
      member_ids: operationData.member_ids || []
    };
  },

  // Get Operation Status Badge
  getOperationStatusBadge(operation) {
    switch (operation.status) {
      case 'pending':
        return { text: 'Pending', class: 'pending', color: '#d69e2e' };
      case 'approved':
        return { text: 'Approved', class: 'approved', color: '#38a169' };
      case 'in_progress':
        return { text: 'In Progress', class: 'in-progress', color: '#3182ce' };
      case 'paused':
        return { text: 'Paused', class: 'paused', color: '#d69e2e' };
      case 'completed':
        return { text: 'Completed', class: 'completed', color: '#38a169' };
      case 'failed':
        return { text: 'Failed', class: 'failed', color: '#e53e3e' };
      case 'cancelled':
        return { text: 'Cancelled', class: 'cancelled', color: '#718096' };
      default:
        return { text: 'Unknown', class: 'unknown', color: '#718096' };
    }
  },

  // Calculate Operation Progress
  calculateOperationProgress(operation) {
    if (!operation.total_members || operation.total_members === 0) {
      return 0;
    }
    
    const processed = operation.processed_members || 0;
    return Math.round((processed / operation.total_members) * 100);
  },

  // Format Operation for Display
  formatOperation(operation) {
    return {
      ...operation,
      progress: this.calculateOperationProgress(operation),
      status_badge: this.getOperationStatusBadge(operation),
      formatted_created_at: operation.created_at ? 
        new Date(operation.created_at).toLocaleString() : 'Unknown',
      formatted_started_at: operation.started_at ? 
        new Date(operation.started_at).toLocaleString() : 'Not started',
      formatted_completed_at: operation.completed_at ? 
        new Date(operation.completed_at).toLocaleString() : 'Not completed',
      duration: operation.duration || 'N/A'
    };
  }
};

export { enhancedInvoiceService };
export default enhancedInvoiceService;
